import { Injector } from "@angular/core";
import { AsyncHelper } from "src/app/helpers/async.helper";
import { IFile } from "src/app/interfaces/io/file.interface";
import { FileModel } from "src/app/models/io/file-model";
import { IFileDriver } from "src/app/services/io/drivers/file-driver.interface";
import { IconsService } from "src/app/services/ux/icons.service";

export class AjaxStorageDriver implements IFileDriver {
  private baseUrl: string;
  private headers: Record<string, string> = null;
  private unit: string;
  private readOnly = true;
  private iconSv: IconsService;

  isReadOnly(): boolean {
    return this.readOnly;
  }

  async mount(unit: string, settingsUrl: string, injector?: Injector): Promise<any> {
    const response = await fetch(settingsUrl);
    const settings = await response.json();

    this.iconSv = this.iconSv || injector.get(IconsService);

    this.baseUrl = settings.url;
    this.unit = unit;

    if (!this.baseUrl.endsWith("/")) {
      this.baseUrl += "/";
    }

    const info = await this.get("mount");
    if (!info) {
      throw new Error("Error mounting device.");
    }

    this.readOnly = info.readOnly;
  }

  getDriveType(): string {
    return "Ajax Storage";
  }

  async list(path: string): Promise<IFile[]> {
    const result = await this.post("list", { path: this.parsePath(path) });

    let list: IFile[];

    if (!Array.isArray(result)) {
      list = result.list;
    } else {
      list = result;
    }

    list = list.map(m => this.toFileModel(m));

    return list;
  }

  async exists(path: string): Promise<boolean> {
    const result = await this.post("exists", { path: this.parsePath(path) });

    if (typeof result !== "boolean") {
      return result.exists;
    }

    return result;
  }

  async rmdir(path: string): Promise<void> {
    await this.post("rmdir", { path });
  }

  async unlink(path: string): Promise<void> {
    await this.post("unlink", { path });
  }

  async readTextFile(path: string): Promise<string> {
    const result = await this.post("read", { path });

    if (typeof result !== "string") {
      return result.content;
    }

    return result;
  }

  async writeTextFile(path: string, content: string): Promise<void> {
    await this.post("write", { path, content });
  }

  private parsePath(path: string) {
    return path.replace(this.unit + ":", "");
  }

  private async get(url: string) {
    const finalUrl = this.parseUrl(url);
    const req: RequestInit = {
      method: "GET",
      mode: "cors",
      cache: "no-cache",
      headers: this.headers || {},
    };

    const response = await fetch(finalUrl, req);

    if (response.ok) {
      const responseData = await response.json();
      return responseData;
    }
    else {
      throw new Error(response.statusText);
    }
  }

  private async post(url: string, data: any) {
    const finalUrl = this.parseUrl(url);
    const req: RequestInit = {
      body: JSON.stringify(data),
      method: "POST",
      mode: "cors",
      cache: "no-cache",
      headers: {
        "Content-Type": "application/json",
      },
    };

    if (this.headers) {
      Object.assign(req.headers, this.headers);
    }

    const response = await fetch(finalUrl, req);

    if (response.ok) {
      const responseData = await response.json();
      return responseData;
    }
    else {
      throw new Error(response.statusText);
    }
  }

  private parseUrl(url: string) {
    return this.baseUrl + url;
  }

  private toFileModel(file: any) {
    /** @todo Assign icons shouldn't be responsability of the driver */
    const model = new FileModel(file);
    if (!model.icon) {
      model.icon = this.iconSv.getIconByFormat(file.path);
    }
    return model;
  }
}
